import type { Favorites, StateProducts, Variants } from '../../sharedTypes';
import { PRODUCTS, type ProductsAction } from '../actions/productsActions';

export type ProductsState = {
  displayedProductIds: string[];
  sponsoredProductIds: string[];
  products: StateProducts;
  variants: Variants;
  totalProductCount: number;
  fetchingProducts: boolean;
  productsRequestFailed: boolean;
  favorites: Favorites;
  shouldDisplaySponsoredPopupWarning: boolean;
  shouldDisplaySponsoredAccordionWarning: boolean;
  shouldDisplayRemoveGWPDisqualifyPopup: boolean;
  shouldDisplayRemoveGWPDowngradePopup: boolean;
  shouldDisplayRemoveGWPOutOfStockPopup: boolean;
  shouldDisplayRemoveGWPWarningPopup: boolean;
  variantToRemove: string | null;
  overrideDefaultVariantId: string | null;
};

export const productsInitialState = {
  displayedProductIds: [],
  sponsoredProductIds: [],
  products: {},
  variants: {},
  totalProductCount: 0,
  fetchingProducts: true,
  productsRequestFailed: false,
  favorites: {},
  shouldDisplaySponsoredPopupWarning: false,
  shouldDisplayRemoveGWPDisqualifyPopup: false,
  shouldDisplayRemoveGWPDowngradePopup: false,
  shouldDisplayRemoveGWPOutOfStockPopup: false,
  shouldDisplayRemoveGWPWarningPopup: false,
  shouldDisplaySponsoredAccordionWarning: false,
  variantToRemove: null,
  overrideDefaultVariantId: null,
  bestSellingSortedIds: [],
  fetchingSearchProducts: false,
};

export function productsReducer(
  state: ProductsState = productsInitialState,
  action: ProductsAction,
): ProductsState {
  switch (action.type) {
    case PRODUCTS.REQUEST_PRODUCTS: {
      const { offset } = action.payload;
      const newDisplayedProductIds = offset === 0 ? [] : state.displayedProductIds;
      return {
        ...state,
        fetchingProducts: true,
        productsRequestFailed: false,
        displayedProductIds: newDisplayedProductIds,
      };
    }

    case PRODUCTS.REQUEST_BEST_SELLING_PRODUCTS: {
      return {
        ...state,
        fetchingProducts: true,
        productsRequestFailed: false,
        displayedProductIds: [],
      };
    }

    case PRODUCTS.REQUEST_PRODUCTS_BY_ID: {
      return {
        ...state,
        fetchingProducts: true,
      };
    }

    case PRODUCTS.REQUEST_SEARCH_PRODUCTS: {
      return {
        ...state,
        fetchingSearchProducts: true,
      };
    }

    case PRODUCTS.FAILED_PRODUCTS_REQUEST: {
      return {
        ...state,
        productsRequestFailed: true,
        fetchingProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_PRODUCTS: {
      const { products, variants, productIds, totalProductCount } = action.payload;

      return {
        ...state,
        ...addProductsAndVariants(state.products, products, state.variants, variants),
        displayedProductIds: productIds,
        totalProductCount,
        fetchingProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_BEST_SELLING_PRODUCTS: {
      const { products, variants, productIds, totalProductCount, sortedProductIds } =
        action.payload;
      return {
        ...state,
        ...addProductsAndVariants(state.products, products, state.variants, variants),
        displayedProductIds: productIds,
        totalProductCount,
        fetchingProducts: false,
        bestSellingSortedIds: sortedProductIds,
      };
    }

    case PRODUCTS.RECEIVE_MORE_PRODUCTS: {
      const { products, variants, productIds } = action.payload;

      return {
        ...state,
        ...addProductsAndVariants(state.products, products, state.variants, variants),
        displayedProductIds: [...state.displayedProductIds, ...productIds],
        fetchingProducts: false,
        fetchingSearchProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_SEARCH_PRODUCTS: {
      const { products, variants } = action.payload;
      return {
        ...state,
        ...addProductsAndVariants(state.products, products, state.variants, variants),
        fetchingSearchProducts: false,
      };
    }

    case PRODUCTS.RECEIVE_SPONSORED_PRODUCTS: {
      const { products, variants, productIds } = action.payload;

      return {
        ...state,
        ...addProductsAndVariants(state.products, products, state.variants, variants),
        sponsoredProductIds: productIds,
      };
    }

    case PRODUCTS.RECEIVE_ALL_PRODUCTS:
    case PRODUCTS.RECEIVE_CART_PRODUCTS: {
      const { products, variants } = action.payload;
      // We don't want to overwrite products already in state,
      // because the products in this payload include variants which,
      // potentially, have been deleted. And we don't want to display
      // deleted variants in the product grid.
      // This is a temporary solution, the problem should ultimately
      // be resolved on the back-end. Once that happens this change should
      // be undone.
      const { products: newProducts, variants: newVariants } =
        addProductsAndVariantsWithoutOverwritingProducts(
          state.products,
          products,
          state.variants,
          variants,
        );

      return {
        ...state,
        products: newProducts,
        variants: newVariants,
      };
    }

    case PRODUCTS.SET_FAVORITES: {
      const favorites = {};
      action.favorites.forEach((productId) => {
        favorites[productId] = true;
      });
      return { ...state, favorites };
    }

    case PRODUCTS.RECEIVE_TOGGLED_FAVORITE: {
      const { productId, liked } = action.payload;
      return {
        ...state,
        favorites: {
          ...state.favorites,
          [productId]: liked,
        },
      };
    }

    case PRODUCTS.TOGGLE_SOLDOUT: {
      const { variantId, soldOut } = action;
      const { variants } = state;
      return {
        ...state,
        variants: {
          ...variants,
          [variantId]: {
            ...variants[variantId],
            soldOut,
          },
        },
      };
    }

    case PRODUCTS.DISPLAY_SPONSORED_POPUP_WARNING: {
      return {
        ...state,
        shouldDisplaySponsoredAccordionWarning: false,
        shouldDisplaySponsoredPopupWarning: true,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_REMOVE_GWP_DISQUALIFY_POPUP: {
      return {
        ...state,
        shouldDisplaySponsoredAccordionWarning: false,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: true,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_REMOVE_GWP_DOWNGRADE_POPUP: {
      return {
        ...state,
        shouldDisplaySponsoredAccordionWarning: false,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: true,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_REMOVE_GWP_OUT_OF_STOCK_POPUP: {
      return {
        ...state,
        shouldDisplaySponsoredAccordionWarning: false,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: true,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_REMOVE_GWP_WARNING_POPUP: {
      return {
        ...state,
        shouldDisplaySponsoredAccordionWarning: false,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: true,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.DISPLAY_SPONSORED_ACCORDION_WARNING: {
      return {
        ...state,
        shouldDisplaySponsoredAccordionWarning: true,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: action.variantId,
      };
    }

    case PRODUCTS.HIDE_SPONSORED_WARNING: {
      return {
        ...state,
        shouldDisplaySponsoredAccordionWarning: false,
        shouldDisplaySponsoredPopupWarning: false,
        shouldDisplayRemoveGWPDisqualifyPopup: false,
        shouldDisplayRemoveGWPDowngradePopup: false,
        shouldDisplayRemoveGWPOutOfStockPopup: false,
        shouldDisplayRemoveGWPWarningPopup: false,
        variantToRemove: null,
      };
    }

    case PRODUCTS.SET_OVERRIDE_DEFAULT_VARIANT_ID: {
      return {
        ...state,
        overrideDefaultVariantId: action.variantId,
      };
    }

    default: {
      return state;
    }
  }
}

function addNewVariants(addedVariants, prevVariants) {
  const newVariants = { ...prevVariants };

  for (const variantId in addedVariants) {
    // below is a temporary fix for parsing descriptors
    // the root of the wrong format in descriptors comes from shop table
    // aleady we have some data in db box which have wrong fomat, below lins is goign to fix that
    const newDescriptors = addedVariants[variantId].variantDescriptors.map((variantDescriptor) => {
      return {
        ...variantDescriptor,
        descriptor: variantDescriptor.descriptor
          .replace(/&#039;/g, "'")
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"'),
      };
    });

    newVariants[variantId] = {
      ...addedVariants[variantId],
      variantDescriptors: newDescriptors,
      isSoldOutOnFetch: addedVariants[variantId].soldOut,
    };
  }
  return newVariants;
}

function addProductsAndVariants(
  prevProducts: StateProducts,
  addedProducts: StateProducts,
  prevVariants: Variants,
  addedVariants: Variants,
) {
  // Unfortunately, flow doesn't handle copying objects with spread
  // when the second spread object has an indexer. Hopefully they'll
  // fix this soon. See https://medium.com/flow-type/spreads-common-errors-fixes-9701012e9d58
  const newProducts = { ...prevProducts };

  for (const productId in addedProducts) {
    newProducts[productId] = addedProducts[productId];
  }

  const newVariants = addNewVariants(addedVariants, prevVariants);
  return { products: newProducts, variants: newVariants };
}

function addProductsAndVariantsWithoutOverwritingProducts(
  prevProducts: StateProducts,
  addedProducts: StateProducts,
  prevVariants: Variants,
  addedVariants: Variants,
) {
  // Unfortunately, flow doesn't handle copying objects with spread
  // when the second spread object has an indexer. Hopefully they'll
  // fix this soon. See https://medium.com/flow-type/spreads-common-errors-fixes-9701012e9d58
  const newProducts = { ...prevProducts };
  for (const productId in addedProducts) {
    // eslint-disable-next-line no-prototype-builtins
    if (!newProducts.hasOwnProperty(productId)) {
      newProducts[productId] = addedProducts[productId];
    }
  }
  const newVariants = addNewVariants(addedVariants, prevVariants);
  return { products: newProducts, variants: newVariants };
}
